import type { UpgradeRequirements, BaseEntity, ResourceBundle } from '@/types/EntityTypes';
import type { GameState } from './GameState';


export abstract class Entity implements BaseEntity {
    id: string;
    name: string;
    description: string;
    cost: number;

    storage?: ResourceBundle;
    consumes?: ResourceBundle;
    produces?: ResourceBundle;

    progressionRequirements?: UpgradeRequirements;
  
    constructor(params: {
      id: string;
      name: string;
      description: string;
      cost: number;
      storage?: ResourceBundle;
      consumes?: ResourceBundle;
      produces?: ResourceBundle
      progressionRequirements?: UpgradeRequirements;
    }) {
      this.id = params.id;
      this.name = params.name;
      this.description = params.description;
      this.cost = params.cost;
      this.storage = params.storage;
      this.consumes = params.consumes;
      this.produces = params.produces;
      this.progressionRequirements = params.progressionRequirements;
    }
  
    canAfford(money: number): boolean {
      return money >= this.cost;
    }
  
    canBeBuilt(gameState: GameState): boolean {
      // Check if the required upgrade is purchased
      if (this.progressionRequirements?.upgrades && this.progressionRequirements.upgrades.some(upg => !gameState.upgrades[upg])) {
        return false;
      }

      // Check if personnel requirements are met
      if (this.progressionRequirements?.personnel) {
        for (const [personnelType, required] of Object.entries(this.progressionRequirements.personnel)) {
          const current = gameState.personnel[personnelType as keyof typeof gameState.personnel] || 0;
          if (current < (required as number)) {
            return false;
          }
        }
      }

      return true;
    }

    meetsNonPersonnelRequirements(gameState: GameState): boolean {
      // Check if the required upgrade is purchased (excluding personnel)
      if (this.progressionRequirements?.upgrades && this.progressionRequirements.upgrades.some(upg => !gameState.upgrades[upg])) {
        return false;
      }

      // Check infrastructure requirements if any
      if (this.progressionRequirements?.infrastructure) {
        for (const [infraType, required] of Object.entries(this.progressionRequirements.infrastructure)) {
          const current = gameState.infrastructure[infraType as keyof typeof gameState.infrastructure] || 0;
          if (current < (required as number)) {
            return false;
          }
        }
      }

      return true;
    }

    getMissingRequirements(gameState: GameState): string[] {
      const missing: string[] = [];

      if (!this.progressionRequirements) {
        return missing;
      }

      const { upgrades, personnel, infrastructure } = this.progressionRequirements;

      // Check upgrade requirements
      if (upgrades) {
        for (const requiredUpgrade of upgrades) {
          if (!gameState.upgrades[requiredUpgrade]) {
            missing.push(`Missing upgrade: ${requiredUpgrade}`);
          }
        }
      }

      // Check personnel requirements
      if (personnel) {
        for (const [role, required] of Object.entries(personnel)) {
          const current = (gameState.personnel as any)[role] || 0;
          if (current < required) {
            missing.push(`Need ${required - current} more ${role} (have ${current}/${required})`);
          }
        }
      }

      // Check infrastructure requirements
      if (infrastructure) {
        for (const [infra, required] of Object.entries(infrastructure)) {
          const current = (gameState.infrastructure as any)[infra] || 0;
          if (current < required) {
            missing.push(`Need ${required - current} more ${infra} (have ${current}/${required})`);
          }
        }
      }

      return missing;
    }

    getDisplayText(): string {
        return `Entity created ${this.name} ($${this.cost})`;
    }

    shouldDegrade(gameState: GameState): boolean {
        return (gameState.resources.money || 0) < 0;
    }
  }
